import { useEffect, useState } from "react";
import {
  loadResx,
  createTranslator,
} from "../../shared/helpers/resourceLoader";
import { useLocalization } from "../../context/LocalizationContext";
import { getCurrencySymbol } from "../../utils/currencySymbols";
import { useLocation } from "react-router-dom";
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import RepairOrderStatuses from "../RepairOrder/RepairOrderStatuses";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import { showErrorMessage } from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import LoadingOverlay from "../common/LoadingOverlay";
import { formatPhoneNumber } from "../../shared/helpers/formUtils";
import InfoCards from "../ServiceDeskUser/common/InfoCards";
import { useVehicleContext } from "../../context/VehicleContext";
import PriceSummary from "./common/PriceSummary";
import ServiceTableRow from "./common/ServiceTableRow";
import ServiceListDisplay from "./common/ServiceListDisplay";
import useServiceFetching from "../../hooks/useServiceFetching";
import PrintableRepairOrder from "./common/PrintableRepairOrder";
import useServiceTotals from "../../hooks/useServiceTotals";

const CompletePage = () => {
  const ctx = useVehicleContext();
  const {
    customerId,
    vehicle: propVehicle,
    status,
    setStatus,
    selectedServices,
    setSelectedServices,
    repairOrder,
    setRepairOrder,
  } = ctx;
  const [originalSelectedServices, setOriginalSelectedServices] = useState([]);
  const { t } = useLocalization();
  const location = useLocation();
  const vehicleFromState = location?.state?.vehicle || {};
  const navCustomerId = location?.state?.customerId;
  const navVehicleId = location?.state?.vehicleId;
  const id = navCustomerId || customerId || null;
  const vehicleId = navVehicleId || (propVehicle && propVehicle.id) || null;
  const [vehicle, setVehicle] = useState(
    propVehicle ?? vehicleFromState ?? null,
  );
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("oem");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [selectedDealershipId] = useSelectedDealership();
  const dealershipId = selectedDealershipId;
  const { services, isLoading: servicesLoading } = useServiceFetching(
    vehicleId,
    dealershipId,
    t,
  );

  const isLoading = loading || servicesLoading;
  const serviceError = error || services[activeTab]?.error;
  const [orderData, setOrderData] = useState(null);
  const repairOrderId =
    (repairOrder && (repairOrder.id || repairOrder.data)) ??
    location?.state?.repairOrderId ??
    null;
  const existingRepairOrderId = location?.state?.repairOrderFromInProgress;
  const currentYear = new Date().getFullYear();
  const [customer, setCustomer] = useState(null);
  const statusDisplayMap = {
    [ServiceOrderStatus.NEW]: RepairOrderStatuses.NEW,
    [ServiceOrderStatus.SIGNED]: RepairOrderStatuses.SIGNED,
    [ServiceOrderStatus.IN_PROGRESS]: RepairOrderStatuses.IN_PROGRESS,
    [ServiceOrderStatus.COMPLETED]: RepairOrderStatuses.COMPLETED,
    [ServiceOrderStatus.PENDING_APPROVAL]: RepairOrderStatuses.PENDING_APPROVAL,
    [ServiceOrderStatus.APPROVAL_EXPIRED]: RepairOrderStatuses.APPROVAL_EXPIRED,
  };

  const getStatusDisplay = (statusCode) => {
    const normalizedStatus = statusCode;
    return statusDisplayMap[normalizedStatus] || statusCode;
  };

  useEffect(() => {
    if (propVehicle && Object.keys(propVehicle).length > 0) {
      if (!vehicle || propVehicle.id !== vehicle.id) {
        setVehicle(propVehicle);
      }
    }
  }, [propVehicle]);

  useEffect(() => {
    if (
      status === ServiceOrderStatus.IN_PROGRESS &&
      originalSelectedServices.length === 0 &&
      selectedServices.length > 0
    ) {
      setOriginalSelectedServices(selectedServices.map((s) => ({ ...s })));
    }
    if (
      (status === ServiceOrderStatus.SIGNED ||
        status === ServiceOrderStatus.NEW) &&
      originalSelectedServices.length > 0
    ) {
      setOriginalSelectedServices([]);
    }
  }, [status, selectedServices, originalSelectedServices.length]);

  const { calculateServiceTypeTotal } = useServiceTotals(selectedServices);

  // Exclude declined services (status === 3) from totals
  const activeSelectedServices = selectedServices;
  const nonDeclinedServices = activeSelectedServices.filter(
    (service) => service.status !== 3,
  );
  const subtotalAmount = nonDeclinedServices.reduce(
    (sum, service) => sum + (parseFloat(service.price) || 0),
    0,
  );
  const taxRate = Number(dealership?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;

  const isServiceSelected = (service) => {
    return selectedServices.some(
      (s) =>
        (s.id && service.id && s.id === service.id) ||
        (s.serviceId && service.serviceId && s.serviceId === service.serviceId),
    );
  };

  const handleServiceSelect = (service, type) => {
    if (!isServiceSelected(service)) {
      const newService = {
        ...service,
        id: service.id || `service-${Date.now()}`,
        type: type,
      };
      setSelectedServices([...selectedServices, newService]);
    }
  };

  const handleRemoveService = (serviceId) => {
    setSelectedServices(
      selectedServices.filter(
        (service) =>
          service.id !== serviceId && service.serviceId !== serviceId,
      ),
    );
  };

  useEffect(() => {
    const fetchDealership = async () => {
      try {
        showLoader();
        const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
          id: dealershipId,
        });
        if (response.status == 200) {
          const data = response.data;
          setDealership(data);
          setCurrencyCode(data.currencyCode || "USD");
        } else {
          setDealership(null);
          setCurrencyCode("USD");
          showErrorMessage(t("error_fetch_dealership"));
        }
      } catch (err) {
        showErrorMessage(err.message);
      } finally {
        hideLoader();
      }
    };
    fetchDealership();

    const fetchDataAndServices = async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchDealership();
        try {
          showLoader();
          const customerResponse = await apiManager.post(
            API_ENDPOINTS.CUSTOMER_GET_BY_ID,
            { id },
          );
          if (customerResponse.status == 200) {
            const data = customerResponse.data;
            setCustomer(data);
            const vid = vehicle?.id || null;
            const vehicleResponse = await apiManager.post(
              API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
              { id: vid },
            );
            if (vehicleResponse.status == 200) {
              const vehicleData = vehicleResponse.data;
              setVehicle(vehicleData);
            } else {
              showErrorMessage(t("error_fetch_vehicle"));
            }
          } else {
            showErrorMessage(t("error_fetch_customer"));
          }
        } catch (err) {
          showErrorMessage(err);
        } finally {
          hideLoader();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndServices();
  }, [id]);

  useEffect(() => {
    const loadRepairOrderItems = async () => {
      const idToFetch = repairOrder?.[0]?.repairOrderId;
      if (!idToFetch) return;
      try {
        showLoader();
        const payload = { repairOrderId: idToFetch };
        const repairOrderData = await apiManager.post(
          API_ENDPOINTS.REPAIR_ORDER_ITEM,
          payload,
        );
        if (repairOrderData && repairOrderData.data.length > 0) {
          const backendServices = repairOrderData.data.map((item) => ({
            id: item.serviceId || `service-${Date.now()}`,
            serviceId: item.serviceId,
            serviceName: item.serviceName,
            description: item.serviceDescription,
            type: item.serviceCategory,
            price: item.price || 0,
            status: item.status || 1,
            ...item,
          }));
          setSelectedServices(backendServices);
          setOriginalSelectedServices(backendServices.map((s) => ({ ...s })));
        } else {
          setSelectedServices([]);
          setOriginalSelectedServices([]);
        }
      } catch (err) {
        showErrorMessage("Failed to fetch repair order items");
        setSelectedServices([]);
        setOriginalSelectedServices([]);
      } finally {
        hideLoader();
      }
    };

    loadRepairOrderItems();
  }, []);

  useEffect(() => {
    if (repairOrderId || existingRepairOrderId) {
      fetchDataForPrint();
    }
  }, [repairOrderId || existingRepairOrderId]);

  const fetchDataForPrint = async () => {
    if (orderData?.repairOrder?.id) return;
    const effectiveRepairOrderId =
      (repairOrder && (repairOrder.id || repairOrder.data)) ||
      location?.state?.repairOrderId ||
      repairOrderId ||
      existingRepairOrderId;
    if (!effectiveRepairOrderId) {
      return;
    }
    try {
      showLoader();
      const response = await apiManager.post(API_ENDPOINTS.REPAIR_ORDER_PRINT, {
        repairOrderId: effectiveRepairOrderId,
      });
      if (response.status === 200) {
        setOrderData(response.data);
      } else {
        throw new Error(t("error_fetch_repair_order"));
      }
    } catch (err) {
      showErrorMessage(err.message || t("error_load_order_details"));
    } finally {
      hideLoader();
    }
  };

  const dealerLogoSrc = "/assets/images/logo/kia-logo.png";
  if (serviceError)
    return <div className="alert alert-danger">{serviceError}</div>;

  return (
    <div>
      {isLoading && <LoadingOverlay />}
      <div className="container-fluid p-0 mb-2">
        <div className="row">
          <div className="col-md-6">
            <div style={{ fontWeight: "bold", fontSize: 18 }}>
              {t("status")}:{" "}
              <span style={{ color: "#ff9800" }}>
                {getStatusDisplay(status)}
              </span>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => window.print()}
              >
                {t("print_pdf")}
              </button>
              <button
                className="btn btn-success"
                onClick={() => {
                  setStatus(ServiceOrderStatus.CLOSED);
                  if (ctx && ctx.stepper && ctx.stepper.current)
                    ctx.stepper.current.to(7);
                }}
              >
                {t("close_service")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="select-services-content">
        <div className="container-fluid">
          <InfoCards t={t} vehicle={vehicle} customer={customer} />
          <div className="col-md-12">
            <div className="card mb-4">
              <PriceSummary
                t={t}
                currencyCode={currencyCode}
                calculateServiceTypeTotal={calculateServiceTypeTotal}
              />
              <div className="card-body">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6 tab-content-card">
                      <ServiceListDisplay
                        services={services}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isServiceSelected={isServiceSelected}
                        onServiceSelect={handleServiceSelect}
                        currencyCode={currencyCode}
                        selectDisabled={true}
                        t={t}
                      />
                    </div>
                    <div className="col-lg-6 selected-card">
                      <h2>{t("selected")}</h2>
                      <table className="table">
                        <tbody>
                          {activeSelectedServices.length > 0 ? (
                            activeSelectedServices.map((service, index) => (
                              <ServiceTableRow
                                key={`selected-${service.id || index}`}
                                service={service}
                                index={index}
                                currencyCode={currencyCode}
                                onRemove={() =>
                                  handleRemoveService(
                                    service.id || service.serviceId,
                                  )
                                }
                                canRemove={false}
                                canEdit={false}
                                t={t}
                              />
                            ))
                          ) : (
                            <tr className="no-services-text">
                              <td colSpan="4" className="text-center py-3">
                                {t("no_services_selected")}
                              </td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{ borderTop: "2px solid #dee2e6" }}
                            >
                              <td></td>
                              <td className="text-start text-muted">
                                {t("tax")} ({taxRate}%)
                              </td>
                              <td className="mulish-extra-bold text-muted">
                                {getCurrencySymbol(currencyCode)}
                                {taxAmount.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{
                                borderTop: "1px solid #dee2e6",
                                fontSize: "1.05rem",
                              }}
                            >
                              <td></td>
                              <td className="text-start fw-bold">
                                {t("total")}
                              </td>
                              <td className="mulish-extra-bold">
                                {getCurrencySymbol(currencyCode)}
                                {totalWithTax.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer bg-gray d-flex justify-content-center align-items-center gap-2"></div>
            </div>
          </div>
        </div>
      </div>
      <PrintableRepairOrder
        orderData={orderData}
        dealerLogoSrc={dealerLogoSrc}
        currentYear={currentYear}
        t={t}
        currencyCode={currencyCode}
      />
    </div>
  );
};

export default CompletePage;
